import { RangeSetBuilder, StateEffect, StateField } from "@codemirror/state";
import { Decoration, DecorationSet, EditorView, hoverTooltip } from "@codemirror/view";
import { IEquationErrorRange } from "./equationErrorRanges";

// Dispatched (see EquationCodeMirrorEditor's errorRanges effect) whenever RankingToolsEditor
// recomputes content errors for the active rank — mirrors setKnownVariablesEffect's shape/usage
// in variableHighlightPlugin.ts.
export const setEquationErrorsEffect = StateEffect.define<IEquationErrorRange[]>();

// `message` is a custom spec key, not one CodeMirror renders itself (unlike `class`/`attributes`)
// — it's just retained on the decoration for equationErrorHoverTooltip below to read back. NOT
// set as a real `title` attribute, since that would show the plain native browser tooltip
// alongside our own styled one.
const errorMark = (message: string) =>
  Decoration.mark({ class: "cm-equationErrorSquiggle", message });

const buildDecorations = (
  ranges: IEquationErrorRange[],
  docLength: number,
): DecorationSet => {
  const builder = new RangeSetBuilder<Decoration>();
  const seen = new Set<string>();
  const sorted = [...ranges].sort((a, b) => a.from - b.from);
  for (const range of sorted) {
    const from = Math.max(0, Math.min(range.from, docLength));
    const to = Math.max(from, Math.min(range.to, docLength));
    if (to <= from) {
      continue;
    }
    const key = `${from}-${to}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    builder.add(from, to, errorMark(range.message));
  }
  return builder.finish();
};

export const equationErrorField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setEquationErrorsEffect)) {
        return buildDecorations(effect.value, tr.state.doc.length);
      }
    }
    return tr.docChanged ? decorations.map(tr.changes) : decorations;
  },
  provide: (field) => EditorView.decorations.from(field),
});

// CodeMirror-native tooltip-on-hover for the error squiggle, styled (see tool-editor.css's
// .cm-equationErrorTooltip) to match the app's AntD tooltip look rather than the plain native
// browser tooltip. Reads the message straight back off whichever error decoration the hovered
// position falls inside — no separate range list to keep in sync. Debouncing/hide-on-move-away
// is handled internally by hoverTooltip itself.
export const equationErrorHoverTooltip = hoverTooltip((view, pos) => {
  let found: { from: number; to: number; message: string } | null = null;
  view.state.field(equationErrorField).between(pos, pos, (from, to, decoration) => {
    const message = (decoration.spec as { message?: string }).message;
    if (message) {
      found = { from, to, message };
      return false;
    }
  });
  if (!found) {
    return null;
  }
  const { from, to, message } = found;
  return {
    pos: from,
    end: to,
    above: true,
    // Renders CodeMirror's own built-in arrow/pointer element (positioned and sized by
    // CodeMirror itself) as a child of our returned dom — recolored in tool-editor.css to match
    // our dark AntD-style background instead of CodeMirror's default light tooltip colors.
    arrow: true,
    create: () => {
      const dom = document.createElement("div");
      dom.className = "cm-equationErrorTooltip";
      dom.textContent = message;
      return { dom };
    },
  };
});
